import { useState } from 'react';
import { Link, NavLink, Outlet } from 'react-router-dom';
import {
  ArrowLeft,
  Database,
  LayoutDashboard,
  Menu,
  ScrollText,
  ShieldCheck,
  Store,
  Users,
} from 'lucide-react';
import { Button, EmptyState, Spinner } from '@adop/ui';
import { useAuth } from '../../lib/auth-context';
import { fetchAdminMe } from '../../lib/admin-api';
import { useAsync } from '../../lib/useAsync';
import { Alert } from '../../components/Alert';
import { Brand } from '../../components/Brand';

interface AdminNavItem {
  to: string;
  label: string;
  icon: typeof LayoutDashboard;
  end?: boolean;
}

const ADMIN_NAV: AdminNavItem[] = [
  { to: '/admin', label: 'Overview', icon: LayoutDashboard, end: true },
  { to: '/admin/restaurants', label: 'Restaurants', icon: Store },
  { to: '/admin/data', label: 'Data', icon: Database },
  { to: '/admin/audit', label: 'Audit', icon: ScrollText },
  { to: '/admin/admins', label: 'Admins', icon: Users },
];

function navLinkClass({ isActive }: { isActive: boolean }): string {
  return `flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
    isActive ? 'bg-zinc-900 text-white' : 'text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900'
  }`;
}

function NeutralPanel() {
  return (
    <div className="mx-auto max-w-lg px-4 py-24">
      <EmptyState
        icon={<ShieldCheck className="h-6 w-6" />}
        title="Not found"
        description="The page you were looking for does not exist."
        action={
          <Link to="/dashboard" className="text-sm font-medium text-zinc-900 underline">
            Back to dashboard
          </Link>
        }
      />
    </div>
  );
}

/**
 * Cosmetic admin gate. The real check is the server's `private.platform_admins`
 * membership on every /api/platform/admin/* request. Non-admins get a neutral
 * not-found panel rather than a redirect, so the route's existence is not leaked.
 */
export function AdminLayout() {
  const { user, profile } = useAuth();
  const me = useAsync(fetchAdminMe, []);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  if (me.loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  if (me.error) {
    return (
      <div className="mx-auto max-w-lg px-4 py-24">
        <Alert>{me.error}</Alert>
        <div className="mt-4">
          <Link to="/dashboard" className="text-sm font-medium text-zinc-900 underline">
            Back to dashboard
          </Link>
        </div>
      </div>
    );
  }

  if (!me.data?.is_admin) return <NeutralPanel />;

  const displayName = profile?.full_name ?? user?.email ?? 'Admin';

  const nav = (
    <nav className="flex flex-col gap-1">
      {ADMIN_NAV.map((item) => {
        const Icon = item.icon;
        return (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            className={navLinkClass}
            onClick={() => setSidebarOpen(false)}
          >
            <Icon className="h-4 w-4" />
            {item.label}
          </NavLink>
        );
      })}
    </nav>
  );

  return (
    <div className="min-h-screen bg-zinc-50">
      <header className="sticky top-0 z-30 border-b border-zinc-200 bg-white">
        <div className="flex items-center gap-3 px-4 py-3">
          <Button
            variant="ghost"
            size="sm"
            className="lg:hidden"
            aria-label="Toggle navigation"
            onClick={() => setSidebarOpen((value) => !value)}
          >
            <Menu className="h-5 w-5" />
          </Button>
          <Brand to="/admin" className="mr-2" />
          <span className="hidden items-center gap-1.5 rounded-full bg-zinc-900 px-2.5 py-1 text-xs font-medium text-white sm:inline-flex">
            <ShieldCheck className="h-3.5 w-3.5" /> Platform admin
          </span>
          <div className="ml-auto flex items-center gap-3">
            <div className="hidden text-right sm:block">
              <p className="text-sm font-medium text-zinc-900">{displayName}</p>
              {user?.email && profile?.full_name ? (
                <p className="text-xs text-zinc-500">{user.email}</p>
              ) : null}
            </div>
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-zinc-900 text-xs font-semibold text-white">
              {displayName.charAt(0).toUpperCase()}
            </span>
          </div>
        </div>
      </header>

      <div className="mx-auto flex max-w-7xl">
        <aside className="hidden w-60 shrink-0 border-r border-zinc-200 bg-white px-3 py-4 lg:block">
          {nav}
          <div className="mt-6 border-t border-zinc-100 pt-4">
            <Link
              to="/dashboard"
              className="flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900"
            >
              <ArrowLeft className="h-4 w-4" /> Back to dashboard
            </Link>
          </div>
        </aside>
        {sidebarOpen ? (
          <div className="fixed inset-0 z-20 lg:hidden" role="dialog" aria-modal="true">
            <div className="absolute inset-0 bg-zinc-900/40" onClick={() => setSidebarOpen(false)} />
            <div className="absolute left-0 top-0 h-full w-64 bg-white p-4 shadow-xl">{nav}</div>
          </div>
        ) : null}
        <main className="min-w-0 flex-1 px-4 py-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
